import { all, call, put, takeEvery, takeLatest } from "redux-saga/effects";

import { showNoti } from "redux/actions";

import {
  CREATE_EVALUATION_REPORT_EXPORT,
  GET_EVALUATION_REPORT_CONFIG,
  GET_EVALUATION_REPORT_EXPORTS,
  RE_EXPORT_EVALUATION_REPORT,
} from "./action";
import {
  createEvaluationReportExportApi,
  getEvaluationReportConfigApi,
  getEvaluationReportExportsApi,
  reExportEvaluationReportApi,
} from "./api";

// Validation errors come back as { error: { field: { message } } } (possibly nested, e.g. scope.projects),
// the admin guard as { error: [{ msg }] }, other errors as { message }
const findErrorMessage = (error) => {
  if (!error) return null;
  if (typeof error === "string") return error;
  if (typeof error.message === "string") return error.message;
  if (typeof error.msg === "string") return error.msg;
  if (typeof error === "object") {
    const values = Array.isArray(error) ? error : Object.values(error);
    for (let i = 0; i < values.length; i += 1) {
      const message = findErrorMessage(values[i]);
      if (message) return message;
    }
  }
  return null;
};

const getResponseMessage = (data, fallbackMessage) =>
  (data && (data.message || findErrorMessage(data.error))) || fallbackMessage;

function* notifyError(message, onError, details, silent) {
  if (!silent) yield put(showNoti({ type: "error", message }));
  if (typeof onError === "function") yield call(onError, message, details);
}

/**
 * Call the API, unwrap { success, data } and report failures through showNoti + onError.
 * onError receives (message, { status }) so callers can react to 400 / 429.
 * silent skips the error toast (background polling).
 */
function* requestSaga(apiCall, args, payload = {}) {
  const { onSuccess, onError, errorMessage, silent } = payload;

  try {
    const response = yield call(apiCall, ...args);
    const data = response && response.data;

    if (!data || data.success === false) {
      yield call(
        notifyError,
        getResponseMessage(data, errorMessage),
        onError,
        { status: response ? response.status : null },
        silent,
      );
      return;
    }

    if (typeof onSuccess === "function") yield call(onSuccess, data.data);
  } catch (error) {
    const response = error && error.response;
    yield call(
      notifyError,
      getResponseMessage(response && response.data, errorMessage),
      onError,
      { status: response ? response.status : null },
      silent,
    );
  }
}

function* getConfigSaga({ payload = {} }) {
  yield call(requestSaga, getEvaluationReportConfigApi, [], payload);
}

function* createExportSaga({ payload = {} }) {
  yield call(
    requestSaga,
    createEvaluationReportExportApi,
    [payload.body],
    payload,
  );
}

function* getExportsSaga({ payload = {} }) {
  yield call(
    requestSaga,
    getEvaluationReportExportsApi,
    [payload.params],
    payload,
  );
}

function* reExportSaga({ payload = {} }) {
  yield call(
    requestSaga,
    reExportEvaluationReportApi,
    [payload.exportId],
    payload,
  );
}

export default function* labelingEvaluationReportSaga() {
  yield all([
    takeLatest(GET_EVALUATION_REPORT_CONFIG, getConfigSaga),
    // every export request must finish and call its callbacks, never cancel one
    takeEvery(CREATE_EVALUATION_REPORT_EXPORT, createExportSaga),
    takeLatest(GET_EVALUATION_REPORT_EXPORTS, getExportsSaga),
    takeEvery(RE_EXPORT_EVALUATION_REPORT, reExportSaga),
  ]);
}
